import { useMutation } from '@tanstack/react-query'
import { apiClient } from '@/lib/api'

export type BillingProduct = 'home-platform' | 'pro-platform' | 'studio-team'
export type BillingCycle = 'monthly' | 'annual'

interface CheckoutSessionResponse {
  sessionId: string
  url: string
}

interface BillingPortalResponse {
  url: string
}

export interface CheckoutSessionInput {
  product: BillingProduct
  billingCycle: BillingCycle
  /**
   * Seat count, only meaningful for `studio-team` (per-seat
   * billing). Backend clamps to a 3-seat minimum regardless
   * of what we send, but wiring the UI control through lets
   * the Stripe checkout page open with the right quantity.
   */
  seats?: number
}

/**
 * Start a Stripe Checkout flow for one of the three paid
 * bundles. On success, redirect the browser straight to the
 * returned Stripe-hosted URL — the user completes payment
 * there, and Stripe sends them to the configured success URL
 * with a `checkout.session.completed` webhook firing shortly
 * after.
 *
 * We don't wait for the webhook here; the returned promise
 * resolves with the redirect URL so callers can
 * `window.location.href = url` (or the caller can stay
 * on-page for testing).
 */
export function useCheckoutSession() {
  return useMutation<CheckoutSessionResponse, Error, CheckoutSessionInput>({
    mutationFn: async input => {
      return apiClient.post<CheckoutSessionResponse>(
        '/api/v1/billing/checkout',
        input,
      )
    },
  })
}

/**
 * Open the Stripe Customer Portal for managing an active
 * subscription (change card, cancel, download invoices).
 * Requires the user to already have a `stripeCustomerId` on
 * their PROFILE row — the backend will 400 otherwise.
 */
export function useBillingPortal() {
  return useMutation<BillingPortalResponse, Error, void>({
    mutationFn: async () => {
      return apiClient.post<BillingPortalResponse>('/api/v1/billing/portal', {})
    },
  })
}
